import { ISavedStateComment } from '@shared/user-state/comments/SavedStateCommentsService';
import { MapWrapper } from '@openlayers/MapWrapper';
import VectorSource from 'ol/source/Vector';
import * as olStyle from 'ol/style';
import Feature from 'ol/Feature';
import { Point as OlPoint } from 'ol/geom';
import { latLonToEN } from '@openlayers/ol-utils';
import VectorImageLayer from 'ol/layer/VectorImage';


/*
 * Displays page state comment drawings as 2d map overlays pinned to location
 * allows moving of the camera without clearing the drawings
 */
export class DrawingsDisplayOpenlayers implements IDrawingsDisplay {
    public enabled = true;
    private comments: ISavedStateComment[] = [];
    private layerId = 'page-view-comments';

    layerSource: VectorSource = null;
    layer: any = null;

    constructor(private mapWrapper: MapWrapper, initialZoom?: number) {
        const existingLayer = this.mapWrapper.getLayerById(this.layerId);
        if (existingLayer) {
            this.layer = existingLayer;
            this.layerSource = existingLayer.getSource();
        } else {
            this.layerSource = new VectorSource({
                attributions: '',
                features: [],
                useSpatialIndex: true,
            });

            this.layer = new VectorImageLayer({
                source: this.layerSource,
                className: 'saved-state-drawings', // this is important so that drawings are excluded from thumbnail
                // openlayers will cull drawings by the anchor point too aggressively, hiding them while part of the image is still on screen
                renderBuffer: 20000,
            });

            this.layer.set('id', this.layerId);
            mapWrapper.addLayer(this.layer, 9999, this.layerId);
        }

        this.initialZoom = initialZoom ?? this.mapWrapper.getZoom();
    }

    initialZoom: number;

    destroy = () => {
        this.mapWrapper.removeLayerById(this.layerId);
        this.layer.setMap(null);
        this.layerSource.clear();
    };

    drawDrawings = () => {
        if (!this.enabled) {
            this.comments.forEach(c => (c.canvasVisible = false));
        }
        this.layerSource.clear();

        const view = this.mapWrapper.getInstance().getView();

        this.comments.forEach(comment => {
            if (comment.canvasVisible && comment.lat && comment.lon) {
                try {
                    const feature = new Feature({
                        geometry: new OlPoint(latLonToEN(comment.lat, comment.lon)),
                    });

                    // drawings are pinned to the zoom they were made at, older ones dont have it stored
                    // so fall back to the zoom of the saved view they belong to
                    const anchorResolution = view.getResolutionForZoom(comment.zoom ?? this.initialZoom);

                    const icon = new olStyle.Icon({ src: comment.canvas, anchor: [0.5, 0.5] });
                    const featureStyle = new olStyle.Style({ image: icon });

                    feature.setId(comment.id);
                    feature.setStyle((_feature, resolution) => {
                        icon.setScale(anchorResolution / resolution);
                        return featureStyle;
                    });

                    this.layerSource.addFeature(feature);
                } catch {}
            }
        });
    };

    setComments(value: ISavedStateComment[]) {
        this.layerSource.clear();
        this.comments = value;
    }

    hideDrawings = () => {
        this.comments.forEach(c => (c.canvasVisible = false));
        this.drawDrawings();
    };

    hideComment(id: number) {
        this.setCommentVisible(id, false);
    }

    showComment(id: number) {
        this.setCommentVisible(id, true);
    }

    panToComment = (id: number) => {
        const comment = this.comments.find(c => c.id === id);
        if (!comment?.lat || !comment?.lon) return;

        const [e, n] = latLonToEN(comment.lat, comment.lon);
        this.mapWrapper.panTo(e, n, comment.zoom ?? this.initialZoom);
    };

    offsetCommentZoom = (id: number, delta: number): number => {
        const comment = this.comments.find(c => c.id === id);
        if (!comment) return null;

        comment.zoom = Number(((comment.zoom ?? this.initialZoom) + delta).toFixed(4));
        this.drawDrawings();

        return comment.zoom;
    };

    private setCommentVisible = (id: number, state: boolean) => {
        if (!this.enabled) return;

        const comment = this.comments.find(c => c.id === id);
        if (!comment) {
            console.warn('no comment with id found', id);
            return;
        }

        comment.canvasVisible = state;
        this.drawDrawings();
    };
}
